export type ThemePreference = "light" | "dark";

export const DEFAULT_THEME: ThemePreference = "light";
export const THEME_PREFERENCES: readonly ThemePreference[] = ["light", "dark"];

const THEME_CACHE_KEY = "msg.theme.preference.v1";
const THEME_COLOR_BY_PREFERENCE: Record<ThemePreference, string> = {
  light: "#f5f7fa",
  dark: "#0b1220",
};

function readLocalStorageItem(key: string): string | null {
  if (typeof globalThis.localStorage === "undefined") {
    return null;
  }

  try {
    return globalThis.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeLocalStorageItem(key: string, value: string): void {
  if (typeof globalThis.localStorage === "undefined") {
    return;
  }

  try {
    globalThis.localStorage.setItem(key, value);
  } catch {
    // Ignore storage quota and privacy-mode errors; theme runtime still works in-memory for this render.
  }
}

function parseThemePreference(value: unknown): ThemePreference | null {
  if (typeof value !== "string") {
    return null;
  }

  const normalized = value.trim().toLowerCase();
  return normalized === "light" || normalized === "dark" ? normalized : null;
}

export function normalizeThemePreference(value: unknown): ThemePreference {
  return parseThemePreference(value) ?? DEFAULT_THEME;
}

export function readCachedTheme(): ThemePreference {
  return normalizeThemePreference(readLocalStorageItem(THEME_CACHE_KEY));
}

export function writeCachedTheme(value: unknown): void {
  const supportedTheme = parseThemePreference(value);
  if (supportedTheme === null) {
    return;
  }
  writeLocalStorageItem(THEME_CACHE_KEY, supportedTheme);
}

function ensureThemeColorMeta(): HTMLMetaElement | null {
  if (typeof document === "undefined") {
    return null;
  }

  const existingMeta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (existingMeta !== null) {
    return existingMeta;
  }

  if (document.head === null) {
    return null;
  }

  const meta = document.createElement("meta");
  meta.setAttribute("name", "theme-color");
  document.head.append(meta);
  return meta;
}

export function applyDocumentTheme(value: unknown): ThemePreference {
  const theme = normalizeThemePreference(value);

  if (typeof document === "undefined") {
    return theme;
  }

  document.documentElement.setAttribute("data-theme", theme);
  document.documentElement.style.colorScheme = theme;

  const meta = ensureThemeColorMeta();
  if (meta !== null) {
    meta.setAttribute("content", THEME_COLOR_BY_PREFERENCE[theme]);
  }

  return theme;
}

export function applyInitialDocumentTheme(): ThemePreference {
  const cachedTheme = readCachedTheme();
  return applyDocumentTheme(cachedTheme);
}
